import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import io from 'socket.io-client';
import ReactMarkdown from 'react-markdown';
import { MapPin, Menu, Mic, MoveUpRight } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

// Styled components for the login form
const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const LoginModal = styled.div`
  width: 647px;
  min-height: 500px;
  max-height: 90vh;
  background: #000000;
  border-radius: 25px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  
  @media (max-width: 768px) {
    width: 90%;
    min-height: 450px;
    padding: 24px;
  }
  
  @media (max-width: 480px) {
    width: 95%;
    min-height: 400px;
    padding: 20px;
  }
`;

const LoginHeader = styled.div`
  margin-bottom: 24px;
  
  h2 {
    color: #ffffff;
    font-size: 25px;
    font-weight: 600;
    margin: 0;
    text-align: center;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 20px;
    
    h2 {
      font-size: 22px;
    }
  }
`;

const LoginForm = styled.form`
  width: 100%;
  max-width: 460px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    color: #ffffff;
    font-size: 16px;
    margin-bottom: 6px;
    font-weight: 500;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 16px;
    
    label {
      font-size: 14px;
      margin-bottom: 5px;
    }
  }
`;

const FormInput = styled.input`
  width: 100%;
  height: 46px;
  background: rgba(217, 217, 217, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 0 15px;
  color: #ffffff;
  font-size: 16px;
  outline: none;
  transition: border-color 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
  
  &:focus {
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  @media (max-width: 480px) {
    height: 44px;
    font-size: 14px;
    padding: 0 16px;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  height: 48px;
  background: #F43F60;
  border: none;
  border-radius: 12px;
  color: #EDEDED;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin-top: 8px;
  
  &:hover:not(:disabled) {
    background: #E03A56;
  }
  
  &:disabled {
    background: #666666;
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  @media (max-width: 480px) {
    height: 44px;
    font-size: 16px;
  }
`;

const Divider = styled.div`
  width: 100%;
  max-width: 460px;
  text-align: center;
  margin: 20px 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
  }
  
  span {
    background: #000000;
    color: #9E9E9E;
    padding: 0 20px;
    font-size: 16px;
    position: relative;
    z-index: 1;
  }
  
  @media (max-width: 480px) {
    margin: 16px 0;
    
    span {
      font-size: 14px;
      padding: 0 16px;
    }
  }
`;

const SocialLogin = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-bottom: 20px;
  
  @media (max-width: 480px) {
    gap: 12px;
    margin-bottom: 16px;
  }
`;

const SocialButton = styled.button`
  width: 100px;
  height: 53px;
  background: #363636;
  border: none;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background: #404040;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
  

  @media (max-width: 768px) {
    width: auto;
    height: 50px;
    padding: 0 16px;
  }
`;

const SignupLink = styled.div`
  text-align: center;
  margin-top: 16px;
  
  span {
    color: #D4D4D4;
    font-size: 16px;
    margin-right: 10px;
  }
  
  .create-account {
    color: #F43F60;
    text-decoration: none;
    font-size: 16px;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  @media (max-width: 480px) {
    margin-top: 12px;
    
    span, .create-account {
      font-size: 14px;
    }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid #F43F60;
  color: #F43F60;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid #22C55E;
  color: #22C55E;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
`;


const Login = () => {
  const navigate = useNavigate();

  // Login form state
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [selectedCar, setSelectedCar] = useState(null);
  const [socket, setSocket] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const [currentOffer, setCurrentOffer] = useState(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });

    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };
  const PUBLIC_VAPID_KEY = "BPBn54Pl2n5iaPnsJB98M63GjwfPtoT6TFNnjzEVo54vJguH7rvboECJXSjTskJhm1Fy8iuv7Vv70pwzpSm8qCA";
  function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/\-/g, "+").replace(/_/g, "/");
  
    const rawData = atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
  }
  const getSubscription = async () => {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      try {
        const registration = await navigator.serviceWorker.register("/sw.js");
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY),
        });

        return subscription;
      } catch (err) {
        console.log(err);
        console.error("Subscription failed:", err);
        return null;
      }
    }
    return null;
  };
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const subscription = await getSubscription();
      console.log(subscription);
      const response = await fetch(process.env.REACT_APP_API_URL + '/api/auth/dealer/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          webPushSubscription: subscription
        })
      });

      const data = await response.json();

      console.log(data);
      if (response.ok) {
        setSuccess('Login successful!');
        // Store token in sessionStorage
        sessionStorage.setItem('dealerToken', data.data.token);
        sessionStorage.setItem('dealer', JSON.stringify(data.data.user));

        // Redirect to dashboard or home page
        setTimeout(() => {
          window.location.href = '/dashboard'; // or wherever you want to redirect
        }, 1500);
      } else {
        setError(data.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // --- UI ---
  return (
    <>
      {/* Loader */}
      {/* <div className="loaders">
        <div className="loader"></div>
      </div> */}
      {/* Header - custom class names to avoid CSS conflicts */}

      {/* Main Content */}
      <main className="main-content">
        <section className="search-area home" style={{ backgroundImage: 'url(/home-bg.png)', backgroundSize: 'cover', backgroundPosition: 'top', backgroundRepeat: 'no-repeat' }}>
          <div className="containers">
            <div className="row justify-content-center" >
              <div className="col-md-12 col-lg-10 col-xl-10" >

                {/* Login Form */}
                <LoginContainer>
                  <LoginModal>
                    <LoginHeader>
                      <h2>Dealer Login</h2>
                    </LoginHeader>

                    <LoginForm onSubmit={handleSubmit}>
                      {error && (
                        <ErrorMessage>
                          {error}
                        </ErrorMessage>
                      )}

                      {success && (
                        <SuccessMessage>
                          {success}
                        </SuccessMessage>
                      )}

                      <FormGroup>
                        <label htmlFor="email">Email</label>
                        <FormInput
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Enter your email"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="password">Password</label>
                        <FormInput
                          type="password"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Enter your password"
                          required
                        />
                      </FormGroup>

                      <LoginButton type="submit" disabled={isLoading}>
                        {isLoading ? 'Logging in...' : 'Log in'}
                      </LoginButton>
                    </LoginForm>

                    <Divider>
                      <span>Or</span>
                    </Divider>

                    <SocialLogin>
                      <SocialButton className="google">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                      </SocialButton>

                      <SocialButton className="apple">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.38-1.08-.52-2.07-.53-3.2 0-1.44.71-2.23.51-3.08-.38C2.79 15.25 3.51 7.59 9.28 7.31c1.33.07 2.25.8 3.04.86.79.05 1.8-.69 3.32-.84 1.8-.19 3.63 1.06 4.59 2.77-4.19 2.51-3.51 9.69.82 10.18zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="white" />
                        </svg>
                      </SocialButton>

                      <SocialButton className="facebook">
                       <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" class="bi bi-facebook" viewBox="0 0 20 20">
                       <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951"/></svg>
                      </SocialButton>
                    </SocialLogin>

                    <SignupLink>
                      <span>Don't have an account?</span>
                      <Link to="/signup" className="create-account">Create account</Link>
                    </SignupLink>
                  </LoginModal>
                </LoginContainer>
              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Footer */}
      <footer className="footer-area">
        <div className="containers">
          <div className="row">
            <div className="col-md-6">
              <div className="copy">
                <p>Copyright © 2024 Automotiveai.com</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="trams">
                <p><a href="#">Privacy Policy</a> | <a href="#">Terms & Conditions</a></p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12">
          <a href="#" className="scrolltotop"><i className="bi bi-arrow-up-circle"></i></a>
        </div>
      </footer>
    </>
  );
};

export default Login; 